import React, { FC, Fragment, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { observer } from "mobx-react";
import Container from "ui/Container/Container";
import { useStore } from "store";
import { useBreakpoint } from "helpers/hooks";
import { cn } from "helpers/style";
import Info from "./components/Info/Info";
import Main from "./components/Main/Main";
import Catalog from "./components/Catalog/Catalog";
import styles from "./Header.module.scss";

const Sticky = dynamic(() => import("common/Header/components/Sticky/Sticky"), {
  ssr: false,
});
const HeaderMobile = dynamic(() => import("common/Header/HeaderMobile"), {
  ssr: false,
});
const CatalogMobile = dynamic(
  () => import("./components/CatalogMobile/CatalogMobile"),
  {
    ssr: false,
  }
);
type Props = {
  contactPhone?: string;
};

const Header: FC<Props> = ({ contactPhone }) => {
  const headerRef = useRef(null);
  const { header, user } = useStore();
  const breakpoint = useBreakpoint(true);
  const isMobile = breakpoint === "mobile";
  const ltLaptop = ["mobile", "tablet"].includes(breakpoint);

  useEffect(() => {
    const target = headerRef.current;
    const observer = new IntersectionObserver(
      entries => {
        headerRef.current &&
          header.toggleStickyHeader(!entries[0].isIntersecting);
      },
      { threshold: 0.05 }
    );
    observer.observe(target);

    return () => {
      observer.disconnect();
      target && observer.unobserve(target);
    };
  }, [header]);

  return (
    <Fragment>
      {!isMobile && (
        <Info
          mobileIsOpen={header.menuMobileOpen}
          contactPhone={contactPhone}
        />
      )}
      <Sticky />
      <header
        className={cn([
          styles.header,
          user.isEmployeeMode && styles.employeeMode,
          header.menuMobileOpen && styles.mobileMenuOpened,
        ])}
        ref={headerRef}
      >
        <Container>
          <Main />
        </Container>
        {!ltLaptop && <Catalog />}
      </header>
      {ltLaptop && (
        <>
          <HeaderMobile />
          {(header.menuMobileOpen || header.catalogMobileOpen) && (
            <CatalogMobile />
          )}
        </>
      )}
    </Fragment>
  );
};

export default observer(Header);
